/**
 * Euchre game sound — a thin, game-flavored facade over the shared DCS sound
 * engine (`@dcs/ui/sound`). This composable owns NO synthesis: it maps the four
 * game moments (deal / play / trick / result) onto the engine's existing 17-role
 * taxonomy and calls `play(role)`. The engine owns the AudioContext, gating,
 * throttle, persistence, reduced-motion auto-mute and iframe fail-closed.
 *
 * Role mapping (no new roles invented — closest semantic fit chosen):
 *   - deal a new hand       → `overlay.open`      (soft filtered-noise sweep ≈ a riffle)
 *   - a card hits the felt  → `interaction.tap`   (percussive noise burst ≈ the card snap)
 *   - a trick resolves      → `interaction.confirm` (soft sine "taken" cue, ≤5×/hand)
 *   - the viewer's team wins → `hero.complete`     (earned three-note rise — the match win)
 *   - the viewer's team loses / spectator end → `notification.info` (neutral bell, never an error klaxon)
 *
 * Games may sit above the portal's subtle band (dcs-product-sound rule 7), so the
 * euchre master volume is seeded higher than the portal's 0.3. Sound is default-ON
 * with a visible persistent mute (owner decision Q-73=A); `configureEuchreSoundDefaults`
 * seeds that ONLY when the user has no stored `dcs.sound` preference yet, so an
 * explicit opt-out is never overwritten on the next visit.
 */
import { configureSoundDefaults, useUiSound } from '@dcs/ui/sound'

/** Euchre master volume — above the portal's subtle 0.3 band, below the 0.8 ceiling. */
const EUCHRE_VOLUME = 0.5

/**
 * Seed euchre's default-ON preference once at app startup (before the first
 * useUiSound()/play()). No-op when the user already has a stored `dcs.sound`
 * preference, so it never clobbers an opt-out. The `hero` category is enabled
 * here deliberately: a match win is exactly the "earned completion" the engine
 * keeps disabled by default, so the game opts it in.
 */
export function configureEuchreSoundDefaults(): void {
  configureSoundDefaults({
    enabled: true,
    userSet: false,
    volume: EUCHRE_VOLUME,
    categories: {
      interaction: true,
      overlay: true,
      navigation: false,
      notification: true,
      hero: true,
    },
  })
}

export interface UseEuchreSound {
  /** Master on/off (writable) for the mute control. */
  enabled: import('vue').WritableComputedRef<boolean>
  /** Toggle master on/off in one call (marks the preference user-explicit). */
  toggleMute: () => void
  /** A new hand was dealt. */
  playDeal: () => void
  /** A card was played onto the felt. */
  playCardPlayed: () => void
  /** A trick resolved to a winner. */
  playTrickWon: () => void
  /**
   * The match ended. `viewerWon` true → the earned win fanfare; false / spectator
   * → a neutral end cue (never an error sound for a friendly loss).
   */
  playGameResult: (viewerWon: boolean) => void
}

export function useEuchreSound(): UseEuchreSound {
  const sound = useUiSound()

  return {
    enabled: sound.enabled,
    toggleMute: sound.toggleMute,
    playDeal: () => sound.play('overlay.open'),
    playCardPlayed: () => sound.play('interaction.tap'),
    playTrickWon: () => sound.play('interaction.confirm'),
    playGameResult: (viewerWon: boolean) =>
      sound.play(viewerWon ? 'hero.complete' : 'notification.info'),
  }
}
